<template>
    <!-- entry on the fly -->
    <div v-if="onAccountingLinesPage" class="w-100">
        <!-- <div>multi</div> -->
        <table class="oss-dense-table" width="100%">
            <tbody>
                <tr>
                    <td align="center" width="10%">
                        <div class="d-flex justify-space-around align-center">
                            <v-btn-icon
                                icon="$fa-magic"
                                size="small"
                                @click.stop="modifyAutoCompletion()"
                                class="ml-2"
                                :color="autoCompletion ? 'primary' : 'grey'"
                            />
                            <v-btn-trash @click="removeItem" class="mr-2" size="small" />
                        </div>
                    </td>
                    <td width="10%">
                        <v-select
                            v-model="log"
                            :items="logs"
                            hide-details
                            outlined
                            :tabindex="(editionIndex + 1) * 100 + 1"
                            size="small"
                            :item-title="(item) => item.text"
                        />
                    </td>
                    <td width="10%">
                        <oss-date-picker
                            v-model="date"
                            :textFieldReadOnly="false"
                            :filterWithoutClosedAccountingPeriods="true"
                            :hide-details="true"
                            :tabindex="(editionIndex + 1) * 100 + 2"
                            @tab="onTabOnDate"
                            autofocus
                        />
                    </td>
                    <td width="10%">
                        <v-text-field
                            v-model="invoiceNumber"
                            hide-details
                            single-line
                            :readonly="readonly"
                            :tabindex="(editionIndex + 1) * 100 + 3"
                            v-if="!bankTransactionId"
                            size="small"
                        />
                    </td>
                    <td width="60%">
                        <div class="fill-width">
                            <table class="oss-dense-table" width="100%">
                                <tbody>
                                    <template v-for="(line, index) in accountingLines" :key="index">
                                        <AccountingItemLine
                                            v-model="accountingLines[index]"
                                            :ref="'line_' + index"
                                            :index="index"
                                            :company="selectedCompany"
                                            :enable-stock="enableStock"
                                            :addQuantityColumn="addQuantityColumn"
                                            :additionalQuantityColumns="additionalQuantityColumns"
                                            :accountableAccounts="accountableAccounts"
                                            :defaultWording="generalWording"
                                            :companyTags="companyTags"
                                            :showDueDateColumn="showDueDateColumn"
                                            :autoCompletion="autoCompletion"
                                            :focusedElement="focusedElement"
                                            :editionIndex="editionIndex"
                                            :onAccountingLinesPage="onAccountingLinesPage"
                                            @insert-line-after="insertLineAfter(index)"
                                            @accountableAccount-changed="
                                                onAccountableAccountChanged(index)
                                            "
                                            @item-credit-changed="onLineAmountSignedChanged(index)"
                                            @item-debit-changed="onLineAmountSignedChanged(index)"
                                            @item-wording-changed="getDisabledValidation"
                                            @focus="onLineFocus"
                                            @remove-line="removeLine"
                                            @previousLine="onPreviousLine"
                                            @nextLine="onNextLine"
                                            @on-first-line-wording-change="updateWording"
                                            @focus-on-date="focusOnDate"
                                            @focus-on-wording="focusOnWording(index)"
                                            @tab-on-credit="tabOnCredit(index)"
                                        />
                                    </template>
                                </tbody>
                            </table>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
        <div class="d-flex justify-end" v-if="!isBalanced">
            <table class="oss-dense-table" width="48%">
                <tbody>
                    <tr>
                        <td width="25%" align="right" class="pr-2">Total</td>
                        <td width="25%">
                            <oss-text-field
                                :hide-details="true"
                                suffix="&#x20ac;"
                                single-line
                                dense
                                outlined
                                readonly
                                disabled
                                :model-value="totalDebit"
                            />
                        </td>
                        <td width="25%">
                            <oss-text-field
                                :hide-details="true"
                                suffix="&#x20ac;"
                                single-line
                                dense
                                outlined
                                readonly
                                disabled
                                :model-value="totalCredit"
                            />
                        </td>
                        <td width="25%">
                            <oss-text-field
                                :hide-details="true"
                                suffix="&#x20ac;"
                                single-line
                                dense
                                outlined
                                readonly
                                :disabled="totalDebit === totalCredit"
                                :error="totalDebit !== totalCredit"
                                :model-value="totalDiff"
                            />
                        </td>
                        <td v-if="showDueDateColumn" />
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
    <!-- entry one by one -->
    <div v-else class="pa-2">
        <!-- <div>only one</div> -->
        <br />
        <div no-gutters class="d-flex pb-3">
            <div class="d-flex" style="width: 15%">
                <v-btn-icon
                    icon="$fa-magic"
                    size="small"
                    @click.stop="modifyAutoCompletion()"
                    :color="autoCompletion ? 'primary' : 'grey'"
                />
                <!-- LOG SELECTION -->
                <v-select-filled
                    v-model="log"
                    :items="logs"
                    hide-details
                    outlined
                    class="pl-2"
                    tabindex="1"
                    density="compact"
                    item-title="text"
                />
            </div>
            <div class="pl-2" style="width: 15%">
                <oss-date-picker-filled
                    v-model="date"
                    :textFieldReadOnly="false"
                    :filterWithoutClosedAccountingPeriods="true"
                    :hide-details="true"
                    label="Date"
                    tabindex="1"
                    autofocus
                    data-cy="Edition-journalDate"
                />
            </div>
            <div style="width: 25%" class="pl-2">
                <v-text-field-filled
                    v-model="generalWording"
                    outlined
                    hide-details
                    tabindex="1"
                    density="compact"
                />
            </div>
            <div class="pl-2" style="width: 15%">
                <v-text-field-filled
                    v-model="invoiceNumber"
                    label="N&#xb0; de pièce"
                    regular
                    hide-details
                    :readonly="readonly"
                    v-if="!bankTransactionId"
                    density="compact"
                />
            </div>
            <div
                class="pl-2"
                style="width: 20%"
                v-if="!documentId && !bankTransactionId && !zTillReceiptId && !expenseReportId"
            >
                <FreeTag
                    :inPrimaryLog="type"
                    :inLogTagId="logTagId"
                    :companyTags="allCompanyTags"
                    :company="selectedCompany"
                    @onTagsChanged="onTagsChanged($event)"
                />
            </div>
            <div class="pl-2 d-flex align-center" style="width: 10%">
                <v-btn-icon
                    icon="$oss-duplicate"
                    size="small"
                    @click.stop="duplicate"
                    v-if="enableDuplicate"
                    v-tooltip="'Dupliquer'"
                />

                <v-btn-icon
                    icon="$fa-arrow-right-left"
                    @click="revert"
                    size="small"
                    v-tooltip="'Inverser le sens de l\'écriture'"
                />
            </div>
        </div>
        <div class="fill-width">
            <table class="oss-dense-table" width="100%">
                <thead>
                    <tr>
                        <!-- <th width="15%">Date</th> -->
                        <th style="text-align: center" width="35%">Compte</th>
                        <th style="text-align: center" width="30%">Libellé</th>
                        <th style="text-align: center" v-if="addQuantityColumn">Produit</th>
                        <th style="text-align: center" v-if="addQuantityColumn">Quantité</th>
                        <th style="text-align: center" width="5%" v-if="additionalQuantityColumns">
                            &#xc9;léments
                        </th>
                        <th width="7%">Débit</th>
                        <th width="7%">Crédit</th>
                        <th v-if="showDueDateColumn">Echéance</th>
                        <th width="7%">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(line, index) in accountingLines" :key="index">
                        <AccountingItemLine
                            v-model="accountingLines[index]"
                            :disabledTrash="accountingLines.length <= 2 || line.isBankTransaction"
                            :ref="'line_' + index"
                            :index="index"
                            :type="type"
                            :company="selectedCompany"
                            :enable-stock="enableStock"
                            :addQuantityColumn="addQuantityColumn"
                            :additionalQuantityColumns="additionalQuantityColumns"
                            :accountableAccounts="accountableAccounts"
                            :defaultWording="generalWording"
                            :companyTags="companyTags"
                            :showDueDateColumn="showDueDateColumn"
                            :autoCompletion="autoCompletion"
                            :focusedElement="focusedElement"
                            @insert-line-after="insertLineAfter(index)"
                            @accountableAccount-changed="onAccountableAccountChanged(index)"
                            @item-credit-changed="onLineAmountSignedChanged(index)"
                            @item-debit-changed="onLineAmountSignedChanged(index)"
                            @item-wording-changed="getDisabledValidation"
                            @focus="onLineFocus"
                            @remove-line="removeLine"
                            @previousLine="onPreviousLine"
                            @nextLine="onNextLine"
                            @focus-on-date="focusOnDate"
                            @focus-on-wording="focusOnWording(index)"
                        />
                    </template>
                    <tr>
                        <td
                            :colspan="2 + (additionalQuantityColumns ? 3 : 0)"
                            align="right"
                            class="pr-2"
                        >
                            Total
                        </td>
                        <td>
                            <oss-text-field
                                :hide-details="true"
                                suffix="&#x20ac;"
                                single-line
                                dense
                                outlined
                                readonly
                                disabled
                                :model-value="totalDebit"
                            />
                        </td>
                        <td>
                            <oss-text-field
                                :hide-details="true"
                                suffix="&#x20ac;"
                                single-line
                                dense
                                outlined
                                readonly
                                disabled
                                :model-value="totalCredit"
                            />
                        </td>
                        <td>
                            <oss-text-field
                                :hide-details="true"
                                suffix="&#x20ac;"
                                single-line
                                dense
                                outlined
                                readonly
                                :disabled="totalDebit === totalCredit"
                                :error="totalDebit !== totalCredit"
                                :model-value="totalDiff"
                            />
                        </td>
                        <td v-if="showDueDateColumn" />
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script lang="ts">
import { AccountingItemEditionBase2 } from "../../AccountingItemEditionBase2";
import AccountingItemLine from "./Line.vue";
import FreeTag from "@/views/tag/FreeTag.vue";
import { Component, toNative } from "vue-facing-decorator";

@Component({
    components: {
        AccountingItemLine,
        FreeTag,
    },
})
export class Edition extends AccountingItemEditionBase2 {}

export default toNative(Edition);
</script>
